<template>
  <div class="page-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">Help Center</h1>
      <p class="page-subtitle">Learn how to use the AlleleBC genomic database and analysis tools</p>
    </div>

    <div class="help-content">
      <!-- 数据库使用指南 -->
      <div class="help-section">
        <h2 class="section-title">Database Usage Guide</h2>

        <div class="content-block">
          <h3>Getting Started</h3>
          <p>AlleleBC provides access to comprehensive genomic variant data including genes, variants, haplotypes, and methylation regions. No registration is required for basic access.</p>

          <h4>Data Browser</h4>
          <ul>
            <li>Use the main search bar to find variants by ID, rsID, or gene name</li>
            <li>Select data type from the dropdown: Variants, Genes, Haplotypes, AMR Regions, or Allele Configurations</li>
            <li>Apply filters using the left panel to narrow down results by chromosome, position, quality scores</li>
            <li>Results are paginated - adjust page size up to 200 records per page for better performance</li>
            <li>Click any row to view detailed information in the side panel</li>
          </ul>

          <h4>Search Tips</h4>
          <ul>
            <li>Gene search: Enter gene symbols like "BRCA1" or Ensembl IDs like "ENSG00000012048"</li>
            <li>Variant search: Use rsIDs like "rs123456" or variant IDs</li>
            <li>Position search: Format as "chr1:12345" for specific chromosomal positions</li>
            <li>Use wildcards and partial matches are supported for gene names</li>
          </ul>

          <h4>Filtering Data</h4>
          <ul>
            <li>Chromosome filter: Select specific chromosomes to focus your search</li>
            <li>Position range: Define start and end positions for regional queries</li>
            <li>Quality filters: Set minimum quality scores for variants</li>
            <li>Read count filters: Filter haplotypes by minimum read coverage</li>
            <li>P-value filters: Filter AMR regions by statistical significance</li>
          </ul>
        </div>
      </div>

      <!-- 分析工具使用 -->
      <div class="help-section">
        <h2 class="section-title">Analysis Tools Usage</h2>

        <div class="content-block">
          <h3>Gene Region Analyzer</h3>
          <p>Analyze variants within and around specific genes:</p>
          <ul>
            <li>Enter a gene name (e.g., BRCA1) or Ensembl ID</li>
            <li>Set upstream and downstream regions in base pairs (default: 5000bp each)</li>
            <li>View all variants found in the extended gene region</li>
            <li>Results show variant positions relative to gene boundaries</li>
          </ul>

          <h3>Batch Variant Query</h3>
          <p>Query multiple variants simultaneously:</p>
          <ul>
            <li>Enter variant IDs one per line (maximum 1000 variants)</li>
            <li>System will return all found variants with their annotations</li>
            <li>Shows success rate (found vs requested variants)</li>
            <li>Results can be exported for further analysis</li>
          </ul>

          <h3>Chromosome Statistics</h3>
          <p>Visualize variant distribution across chromosomes:</p>
          <ul>
            <li>Generates bar charts showing variant counts per chromosome</li>
            <li>Useful for understanding dataset composition</li>
            <li>Helps identify chromosomes with high variant density</li>
          </ul>
        </div>
      </div>

      <!-- 数据导出 -->
      <div class="help-section">
        <h2 class="section-title">Data Export</h2>

        <div class="content-block">
          <h3>Export Formats</h3>
          <ul>
            <li><strong>JSON:</strong> Machine-readable format for programming applications</li>
            <li><strong>CSV:</strong> Spreadsheet-compatible format for Excel, R, Python analysis</li>
          </ul>

          <h3>Export Options</h3>
          <ul>
            <li>Set record limits (up to 100,000 records per export)</li>
            <li>Filter by chromosome to reduce file size</li>
            <li>Export search results or filtered datasets</li>
            <li>Files are downloaded directly to your computer</li>
          </ul>

          <h3>Large Dataset Access</h3>
          <p>For complete database access or datasets larger than export limits, contact our data team for bulk download arrangements.</p>
        </div>
      </div>

      <!-- 数据类型说明 -->
      <div class="help-section">
        <h2 class="section-title">Data Types</h2>

        <div class="content-block">
          <h3>Variants</h3>
          <p>Genetic variations including SNPs and indels with chromosomal positions, reference/alternative alleles, and rsIDs.</p>

          <h3>Genes</h3>
          <p>Gene annotations with genomic coordinates, Ensembl IDs, and associated haplotype count data.</p>

          <h3>Haplotypes</h3>
          <p>Haplotype blocks with variant compositions, read counts, and allelic ratios for haplotype A and B.</p>

          <h3>AMR Regions</h3>
          <p>Allele-specific methylation regions with CpG site counts and statistical significance values.</p>

          <h3>Allele Configurations</h3>
          <p>Paired variant relationships showing different allelic combinations and their genomic contexts.</p>
        </div>
      </div>

      <!-- 常见问题 -->
      <div class="help-section">
        <h2 class="section-title">Frequently Asked Questions</h2>

        <div class="faq-list">
          <div class="faq-item" v-for="(faq, index) in faqs" :key="index">
            <div class="faq-question" @click="toggleFaq(index)">
              <span>{{ faq.question }}</span>
              <span class="faq-toggle">{{ openFaq === index ? '−' : '+' }}</span>
            </div>
            <transition name="faq-expand">
              <div v-if="openFaq === index" class="faq-answer">
                <p>{{ faq.answer }}</p>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- 限制和性能 -->
      <div class="help-section">
        <h2 class="section-title">System Limits & Performance</h2>

        <div class="content-block">
          <h3>Query Limits</h3>
          <ul>
            <li>Maximum 1000 records per page in data browser</li>
            <li>Batch queries limited to 1000 variants per request</li>
            <li>Export limits: 100,000 records per file</li>
            <li>Regional queries limited to 10MB genomic regions</li>
          </ul>

          <h3>Performance Tips</h3>
          <ul>
            <li>Use chromosome filters to improve query speed</li>
            <li>Smaller page sizes load faster for large result sets</li>
            <li>Apply multiple filters to narrow search scope</li>
            <li>For very large datasets, consider using batch queries or exports</li>
          </ul>
        </div>
      </div>

      <!-- 联系支持 -->
      <div class="help-section">
        <h2 class="section-title">Support & Contact</h2>

        <div class="contact-grid">
          <div class="contact-item">
            <h4>Technical Support</h4>
            <p>For database access issues, search problems, or tool malfunctions:</p>
            <p><strong>Email:</strong> support@allelebc.org</p>
          </div>

          <div class="contact-item">
            <h4>Data Questions</h4>
            <p>For questions about data content, annotations, or quality:</p>
            <p><strong>Email:</strong> data@allelebc.org</p>
          </div>

          <div class="contact-item">
            <h4>Collaboration</h4>
            <p>For research partnerships, data submission, or custom analysis:</p>
            <p><strong>Email:</strong> collaborate@allelebc.org</p>
          </div>
        </div>

        <div class="response-info">
          <p><strong>Response Time:</strong> We typically respond within 24-48 hours during business days.</p>
          <p><strong>Citation:</strong> Please cite as "AlleleBC Database (2024). Available at: https://allelebc.org" when using our data in publications.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Help',
  data() {
    return {
      openFaq: null,
      faqs: [
        {
          question: 'How do I search for variants in a specific gene?',
          answer: 'Go to Data Browser, select "Genes" from the dropdown, and enter the gene name or Ensembl ID in the search box. You can also use the Gene Region Analyzer tool to find variants within and around the gene boundaries.'
        },
        {
          question: 'What is the difference between variants and haplotypes?',
          answer: 'Variants are individual genetic differences at specific positions, while haplotypes are groups of variants that are inherited together on the same chromosome. Haplotypes show the phase relationship between variants.'
        },
        {
          question: 'How can I download large datasets?',
          answer: 'Use the export function with appropriate filters to download up to 100,000 records. For larger datasets, contact our data team for bulk access arrangements.'
        },
        {
          question: 'Why do some searches return no results?',
          answer: 'Check your spelling, ensure you are searching the correct data type, and verify that your filters are not too restrictive. Try broader search terms or remove some filters.'
        },
        {
          question: 'Can I access this database programmatically?',
          answer: 'Yes, we provide a REST API for programmatic access. Contact our technical support team for API documentation and access credentials.'
        },
        {
          question: 'How often is the database updated?',
          answer: 'The database is updated quarterly with new annotations and monthly with quality improvements. Critical updates are applied as needed.'
        }
      ]
    }
  },
  methods: {
    // 切换FAQ展开状态
    toggleFaq(index) {
      this.openFaq = this.openFaq === index ? null : index
    }
  }
}
</script>

<style scoped>
/* 页面容器 */
.page-container {
  min-height: calc(100vh - 100px);
  padding: 40px 20px;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 32px;
  color: #1f3a93;
  margin-bottom: 10px;
  font-weight: 600;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
}

/* 帮助内容 */
.help-content {
  max-width: 1000px;
  margin: 0 auto;
}

/* 帮助部分 */
.help-section {
  background: white;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 22px;
  margin-bottom: 20px;
  color: #1f3a93;
  font-weight: 600;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
}

/* 内容块 */
.content-block h3 {
  color: #1f3a93;
  margin: 20px 0 10px 0;
  font-size: 18px;
}

.content-block h4 {
  color: #333;
  margin: 15px 0 8px 0;
  font-size: 16px;
}

.content-block p {
  margin: 10px 0;
  line-height: 1.6;
  color: #555;
}

.content-block ul {
  margin: 10px 0;
  padding-left: 20px;
}

.content-block ul li {
  margin-bottom: 6px;
  line-height: 1.5;
  color: #555;
}

/* FAQ样式 */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  cursor: pointer;
  background: #f8f9fa;
  transition: background-color 0.3s;
  font-weight: 500;
}

.faq-question:hover {
  background: #f0f2f5;
}

.faq-toggle {
  font-size: 18px;
  color: #1f3a93;
  font-weight: bold;
}

.faq-answer {
  padding: 15px 20px;
  background: white;
  border-top: 1px solid #f0f0f0;
}

.faq-answer p {
  margin: 0;
  color: #555;
  line-height: 1.6;
}

/* FAQ展开动画 */
.faq-expand-enter-active,
.faq-expand-leave-active {
  transition: all 0.3s ease;
}

.faq-expand-enter-from {
  opacity: 0;
  max-height: 0;
}

.faq-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

/* 联系网格 */
.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.contact-item {
  padding: 20px;
  background: #f8f9ff;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.contact-item h4 {
  margin: 0 0 10px 0;
  color: #1f3a93;
  font-size: 16px;
}

.contact-item p {
  margin: 5px 0;
  color: #555;
  font-size: 14px;
  line-height: 1.5;
}

.response-info {
  padding: 15px;
  background: #f0f9ff;
  border-radius: 6px;
  border: 1px solid #d0e8ff;
}

.response-info p {
  margin: 5px 0;
  color: #1f3a93;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-container {
    padding: 20px 10px;
  }

  .help-section {
    padding: 20px;
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>